import React, { useCallback, useEffect, useRef } from 'react';
import {
  SAVE_SUCCESS_MODAL_OPEN_EVENT,
  SAVE_SUCCESS_MODAL_CLOSE_EVENT,
  getSaveSuccessModalContext,
  closeSaveSuccessModal
} from '../utils/saveSuccessModalBus';

/**
 * Always-mounted, DOM-controlled modal.
 * Follows the same pattern as FootnoteModal / YearEndChangeModal:
 * - No React state that causes parent re-renders.
 * - Visibility toggled via inline style on a ref.
 * - Context (error count, exit callback) read from the bus on open.
 */
export const SaveSuccessModal: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const exitBtnRef = useRef<HTMLButtonElement>(null);
  const continueBtnRef = useRef<HTMLButtonElement>(null);
  const iconWrapRef = useRef<HTMLDivElement>(null);

  const onExitRef = useRef<(() => void) | null>(null);

  const close = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.style.display = 'none';
    }
  }, []);

  const open = useCallback(() => {
    const ctx = getSaveSuccessModalContext();
    const errorCount = ctx?.errorCount ?? 0;
    onExitRef.current = ctx?.onExit ?? null;

    // Update content via DOM (no state)
    if (titleRef.current) {
      titleRef.current.textContent =
        errorCount === 0 ? 'Form Validated and Saved!' : 'Form Saved as Draft';
    }
    if (descRef.current) {
      descRef.current.textContent =
        errorCount === 0
          ? 'All validation checks passed. Your filing is ready for submission.'
          : `Saved successfully. ${errorCount} validation ${
              errorCount === 1 ? 'issue' : 'issues'
            } remaining.`;
    }
    if (iconWrapRef.current) {
      // Green for clean validated, amber for saved-with-issues
      iconWrapRef.current.style.background =
        errorCount === 0 ? '#dcfce7' : '#fef3c7';
      const svg = iconWrapRef.current.querySelector('svg');
      if (svg) {
        (svg as SVGElement).style.color =
          errorCount === 0 ? '#16a34a' : '#d97706';
      }
    }

    if (rootRef.current) {
      rootRef.current.style.display = 'flex';
    }
  }, []);

  const handleExit = useCallback(() => {
    const onExit = onExitRef.current;
    close();
    if (onExit) onExit();
  }, [close]);

  const handleContinue = useCallback(() => {
    close();
  }, [close]);

  useEffect(() => {
    document.addEventListener(SAVE_SUCCESS_MODAL_OPEN_EVENT, open);
    document.addEventListener(SAVE_SUCCESS_MODAL_CLOSE_EVENT, close);

    return () => {
      document.removeEventListener(SAVE_SUCCESS_MODAL_OPEN_EVENT, open);
      document.removeEventListener(SAVE_SUCCESS_MODAL_CLOSE_EVENT, close);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 bg-black bg-opacity-50 items-center justify-center"
      style={{ display: 'none', zIndex: 10000 }}
    >
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div
            ref={iconWrapRef}
            className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4"
            style={{ background: '#dcfce7' }}
          >
            <svg
              className="h-6 w-6"
              style={{ color: '#16a34a' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3
            ref={titleRef}
            className="text-lg font-semibold text-gray-900 mb-2"
          >
            Form Validated and Saved!
          </h3>
          <p ref={descRef} className="text-sm text-gray-600">
            All validation checks passed. Your filing is ready for submission.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            ref={exitBtnRef}
            type="button"
            onClick={handleExit}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Exit to Filings
          </button>
          <button
            ref={continueBtnRef}
            type="button"
            onClick={handleContinue}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Continue Editing
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveSuccessModal;
